import React from "react";
import { BannerCaseStudy } from "@/.storyblok/types/319751/storyblok-components";
import { storyblokEditable, SbBlokData } from "@storyblok/react";
import Image from "next/image";
import Link from "next/link";

interface BlogCompCaseStudyProps {
  blok?: BannerCaseStudy & SbBlokData;
}

const BannerCaseStudyComponent: React.FC<BlogCompCaseStudyProps> = ({ blok }) => {
  if (!blok) return null;

  const {
    banner_type,
    title,
    description,
    cta_button,
    cta_button_link,
    image,
  } = blok;

  // Add the new red_bg_icon condition
  if (banner_type === "red_bg_icon") {
    return (
      <div {...storyblokEditable(blok)} className="accent-section">
        <div className="container">
          <div className="full-width padding-small image image-right-tool">
            <div className="content-image-right">
              {title && <h2 className="white-text">{title}</h2>}
              {description && <p className="larger-text">{description}</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (banner_type === "banner_white") {
    return (
      <div {...storyblokEditable(blok)} className="banner-blue-chequered py-4">
        <div className="container">
          <div className="banner-content">
            {image?.filename && (
              <Image
                src={image.filename}
                alt={image.alt || title || ""}
                title={image.title || title || ""}
                width={image.width || 219}
                height={image.height || 305}
                className="js-lazy-load bg-white p-4 rounded-4"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                loading="lazy"
                quality={75}
              />
            )}
            {title && <h2 className="text-white">{title}</h2>}
            {description && <p className="lead text-white mb-3">{description}</p>}
            {cta_button && cta_button_link?.url && (
              <Link
                href={cta_button_link.url}
                className="btn btn-lg btn-primary js-cta-btn"
                target={(cta_button_link.linktype as string) === "external" ? "_blank" : "_self"}
                rel={(cta_button_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
              >
                {cta_button}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (banner_type === "banner_white_right") {
    return (
      <div {...storyblokEditable(blok)} className="py-5 mb-4 banner_white_right">
        <div className="container m-3">
          <div className="banner-content">
            {title && <h2 className="text-white">{title}</h2>}
            {description && <p className="lead text-white mb-3 me-3">{description}</p>}
            {image?.filename && (
              <Image
                src={image.filename}
                alt={image.alt || title || ""}
                title={image.title || title || ""}
                width={image.width || 219}
                height={image.height || 305}
                className="js-lazy-load bg-white p-4 rounded-4"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                loading="lazy"
                quality={75}
              />
            )}
            {cta_button && cta_button_link?.url && (
              <Link
                href={cta_button_link.url}
                className="btn btn-lg btn-primary js-cta-btn"
                target={(cta_button_link.linktype as string) === "external" ? "_blank" : "_self"}
                rel={(cta_button_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
              >
                {cta_button}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (banner_type === "center_align") {
    return (
      <div {...storyblokEditable(blok)} className="banner-blue-chequered py-4">
        <div className="container">
          <div className="text-center">
            {title && <h2 className="text-white">{title}</h2>}
            {description && <p className="lead text-white mb-3">{description}</p>}
            {cta_button && cta_button_link?.url && (
              <Link
                href={cta_button_link.url}
                className="btn btn-lg btn-primary"
                target={(cta_button_link.linktype as string) === "external" ? "_blank" : "_self"}
                rel={(cta_button_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
              >
                {cta_button}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (banner_type === "red_bg") {
    return (
      <div {...storyblokEditable(blok)} className="bg-primary text-center py-5">
        <div className="container">
          {cta_button && cta_button_link?.url && (
            <Link
              href={cta_button_link.url}
              className="btn btn-white btn-lg js-cta-btn"
              target={(cta_button_link.linktype as string) === "external" ? "_blank" : "_self"}
              rel={(cta_button_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
            >
              {cta_button}
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (banner_type === "box_cta") {
    return (
      <div {...storyblokEditable(blok)} className="container py-4 py-md-5">
        <div className="numbers-bg-section brand-section component-box-cta rounded-4 p-4 p-lg-5">
          <div className="row align-items-center">
            <div className="col-24 col-lg-10">
              {blok.topic && <span className="pretitle mb-2">{blok.topic}</span>}
              {title && <h2 className="text-white mb-4">{title}</h2>}
              {image?.filename && (
                <Image
                  src={image.filename}
                  alt={image.alt || ""}
                  width={64}
                  height={64}
                  className="img-fluid component-box-cta-image mb-3 mb-lg-0"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              )}
            </div>
            <div className="col-24 col-lg-10 offset-lg-4">
              {description && (
                <p className="component-box-cta-content text-white mb-3">
                  {description}
                </p>
              )}
              {cta_button && cta_button_link?.url && (
                <p className="mt-4 mb-0">
                  <Link
                    href={cta_button_link.url}
                    className="btn btn-lg btn-primary"
                    target={(cta_button_link.linktype as string) === "external" ? "_blank" : "_self"}
                    rel={(cta_button_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
                  >
                    {cta_button}
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (banner_type === "download_now") {
    return (
      <div {...storyblokEditable(blok)} className="data-bg-section">
        <div className="container">
          <div className="padding-medium d-flex flex-column flex-md-row align-items-center">
            <div className="two-thirds margin-bottom-mobile text-center-mobile mb-4 mb-md-0">
              {title && <h2>{title}</h2>}
              {description && <p>{description}</p>}
            </div>
            <div className="one-third flex-align-center text-end text-center-mobile">
              {cta_button && cta_button_link?.url && (
                <Link
                  href={cta_button_link.url}
                  className="button button-large button-primary"
                  target={(cta_button_link.linktype as string) === "external" ? "_blank" : "_self"}
                  rel={(cta_button_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
                >
                  {cta_button}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...storyblokEditable(blok)} className="banner-case-study padding-medium">
      <div className="container">
        {image?.filename ? (
          <Image
            src={image.filename}
            alt={image.alt || title || "Case Study"}
            title={image.title || title || "Case Study"}
            width={image.width || 219}
            height={image.height || 305}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            loading="lazy"
            quality={75}
          />
        ) : (
          <div
            style={{ width: "219px", height: "305px" }}
            className="placeholder-image"
          ></div>
        )}
        <div className="banner-content">
          {title && <h2 className="white-text">{title}</h2>}
          {description && (
            <p className="larger-text text-white margin-bottom-small">
              {description}
            </p>
          )}
          {cta_button && cta_button_link?.url && (
            <Link
              href={cta_button_link.url}
              className="button button-medium button-primary text-white mt-3"
              target={(cta_button_link.linktype as string) === "external" ? "_blank" : "_self"}
              rel={(cta_button_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
            >
              {cta_button}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default BannerCaseStudyComponent;